import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://api.pixlybooth.com';

axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    axios.get(`${API}/api/auth/me`)
      .then(res => setUser(res.data.user))
      .catch(err => {
        // ── Temporary verbose logging for mobile debugging ──
        console.error('[AuthContext] /api/auth/me failed:', {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
          url: `${API}/api/auth/me`,
          code: err.code,
        });
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password, pendingBooking = null) => {
    const res = await axios.post(`${API}/api/auth/login`, { email, password, pendingBooking });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const signup = useCallback(async (name, email, password) => {
    const res = await axios.post(`${API}/api/auth/signup`, { name, email, password });
    return res.data;
  }, []);

  const verifyOTP = useCallback(async (email, otp, pendingBooking = null) => {
    const res = await axios.post(`${API}/api/auth/verify-otp`, { email, otp, pendingBooking });
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(async () => {
    await axios.post(`${API}/api/auth/logout`);
    setUser(null);
  }, []);

  const resendOTP = useCallback(async (email) => {
    const res = await axios.post(`${API}/api/auth/resend-otp`, { email });
    return res.data;
  }, []);

  const hasRole = useCallback((roles) => {
    if (!user) return false;
    const list = Array.isArray(roles) ? roles : [roles];
    return list.includes(user.role);
  }, [user]);

  const hasPermission = useCallback((slug) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!user.permissions) return false;
    if (user.permissions.includes('*')) return true;
    return user.permissions.includes(slug);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, signup, verifyOTP, logout, resendOTP, hasRole, hasPermission, API }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
